import type { ReactNode } from "react";
import type { CheckInCtaState } from "@/features/pulse-surveys/components/check-in-cta/check-in-cta-state";
import { cn } from "@/shared/lib/cn";
import { MOOD_DISPLAY, type MoodOptionId } from "@/shared/pulse/mood/mood-display";

/// Per-mood glow treatment for the "done" orb. Icon/emoji stays the primary
/// signal (color never the only one).
const MOOD_ORB_TINT: Record<MoodOptionId, string> = {
  great: "bg-emerald-50 ring-emerald-300 dark:bg-emerald-950/50 dark:ring-emerald-700",
  good: "bg-sky-50 ring-sky-300 dark:bg-sky-950/50 dark:ring-sky-700",
  okay: "bg-amber-50 ring-amber-300 dark:bg-amber-950/50 dark:ring-amber-700",
  low: "bg-orange-50 ring-orange-300 dark:bg-orange-950/50 dark:ring-orange-700",
  struggling: "bg-rose-50 ring-rose-300 dark:bg-rose-950/50 dark:ring-rose-700",
};

interface Props {
  state: CheckInCtaState;
  /// One-shot celebration ring right after today's check-in completes.
  celebrating?: boolean;
  className?: string;
}

/// The orb itself, pure presentation. Ready: brand-gradient sphere with a
/// slow breath so the eye keeps finding it. In progress: gradient sphere with
/// a progress arc. Done: the day's mood face in its color glow (neutral spark
/// until the shore exposes todayMood). Muted flat disc when read-only,
/// loading, or unavailable.
export function MoodOrb({ state, celebrating = false, className }: Props): ReactNode {
  if (state.kind === "done") {
    const face = state.mood ? MOOD_DISPLAY[state.mood].emoji : "✨";
    return (
      <span
        aria-hidden="true"
        className={cn(
          "inline-flex size-7 items-center justify-center rounded-full text-base ring-2 ring-inset",
          state.mood ? MOOD_ORB_TINT[state.mood] : "bg-pulse-soft ring-violet-300",
          celebrating && "animate-orb-burst",
          className,
        )}
      >
        {face}
      </span>
    );
  }

  if (state.kind === "in_progress") {
    const fraction = state.total > 0 ? Math.min(1, state.answered / state.total) : 0.5;
    return (
      <span aria-hidden="true" className={cn("relative inline-flex size-7", className)}>
        <span className="absolute inset-0 rounded-full bg-pulse opacity-90" />
        <ProgressArc fraction={fraction} />
      </span>
    );
  }

  if (state.kind === "ready") {
    return (
      <span
        aria-hidden="true"
        className={cn(
          "inline-flex size-7 rounded-full bg-pulse shadow-brand",
          "animate-orb-breathe",
          className,
        )}
      >
        {/* Specular highlight so the gradient reads as a sphere, not a dot. */}
        <span className="m-1 size-2 rounded-full bg-white/50 blur-[2px]" />
      </span>
    );
  }

  // read_only / loading / unavailable: a still, muted disc.
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-flex size-7 rounded-full bg-surface-muted ring-1 ring-inset ring-border-strong",
        state.kind === "loading" && "animate-pulse",
        className,
      )}
    />
  );
}

/// Thin conic progress ring over the gradient sphere: "you started, one to
/// go" at a glance.
function ProgressArc({ fraction }: { fraction: number }): ReactNode {
  const angle = Math.round(fraction * 360);
  return (
    <span
      className="absolute inset-[-3px] rounded-full"
      style={{
        // reason: conic-gradient progress needs a computed angle; the colors
        // come from tokens, only the angle is dynamic.
        background: `conic-gradient(var(--pw-violet-500) ${String(angle)}deg, color-mix(in oklab, var(--pw-violet-500) 20%, transparent) 0deg)`,
        mask: "radial-gradient(farthest-side, transparent calc(100% - 3px), black calc(100% - 2px))",
      }}
    />
  );
}
